import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query"
import { getChatHistory, getChatHistoryById, getChatHistoryList, getChatInfo, getChatMemoryDetail, getChatMemoryRoles, getChatStyleList, getInspirationReply, getModels, getPinedList, getRecentChat, getRecentRoom, type ChatMemoryAttribute, type ChatMemoryRolesData } from "../chat"
import { UnwrapPromise } from "@/types"

// 最近的聊天(单聊)
export const RecentChatQueryOption = infiniteQueryOptions<UnwrapPromise<ReturnType<typeof getRecentChat>>['data'], any, any, any, Parameters<typeof getRecentChat>[0]>({
  queryKey: ['recent', 'chat'],
  initialPageParam: { page: 1, page_size: 30, detail: true } as Parameters<typeof getRecentChat>[0],
  queryFn: async ({ pageParam }): Promise<any> => {
    return (await getRecentChat({ ...pageParam })).data
  },
  getNextPageParam: (lastPage: any, allPages, lastPageParam) => {
    if (!lastPage?.length) return null
    return { ...lastPageParam, page: lastPageParam?.page! + 1 }
  },
})

// 最近的聊天(群聊)
export const RecentGroupQueryOption = infiniteQueryOptions<UnwrapPromise<ReturnType<typeof getRecentRoom>>['data'], any, any, any, Parameters<typeof getRecentRoom>[0]>({
  queryKey: ['recent', 'room'],
  initialPageParam: { page: 1, page_size: 30, detail: true } as Parameters<typeof getRecentRoom>[0],
  // @ts-ignore
  queryFn: async ({ pageParam }): Promise<PageData> => {
    return (await getRecentRoom({ ...pageParam })).data
  },
  getNextPageParam: (lastPage: any, allPages, lastPageParam) => {
    if (!lastPage?.length) return null

    return { ...lastPageParam, page: lastPageParam?.page! + 1 }
  },
})


// 
export const chatSessionIdQueryOption = (sessionId) => queryOptions({
  queryKey: ['chats', String(sessionId)],
  queryFn: ({ queryKey }) => getChatInfo({ session_id: sessionId }),
  staleTime: Infinity,
})

export const ModelOption = queryOptions({
  queryKey: ['chats', 'models'],
  queryFn: () => getModels(),
})


// 聊天历史纪录
export const chatHisotryQueryOption = (chatId, sessionId, userId, scriptId?, cb?) => infiniteQueryOptions({
  queryKey: ['chats', 'history', chatId, sessionId, scriptId, userId,],
  queryFn: async ({ pageParam }): Promise<any> => {
    const data = (await getChatHistory({
      chat_id: chatId,
      session_id: sessionId,
      user_id: userId,
      script_chat_id: scriptId,
      ...pageParam
    })).data?.chat_record || []
    data.length > 0 && cb && cb({ pageParam })

    return data
  },
  getNextPageParam: function (lastPage: any[], allPages, lastPageParam): any {
    if (!lastPage?.length) return null
    return { ...lastPageParam, page: lastPageParam.page + 1 }
  },
  initialPageParam: { page: 1, page_size: 20 },
  gcTime: 0
})

// pin的消息列表
export const pinedMessageQueryOption = (sessionId, chatId) => infiniteQueryOptions({
  queryKey: ['chats', 'history', sessionId, chatId],
  queryFn: async ({ pageParam }): Promise<any> => {
    const data = (await getPinedList({
      chat_id: chatId,
      session_id: sessionId,
      ...pageParam
    })).data || []

    return data
  },
  getNextPageParam: function (lastPage: any[], allPages, lastPageParam): any {
    if (!lastPage?.length) return null
    return { ...lastPageParam, page: lastPageParam.page + 1 }
  },
  initialPageParam: { page: 1, page_size: 20 }
})

// 聊天会话历史列表
export const chatHistoryListQueryOption = (sessionId) => infiniteQueryOptions({
  queryKey: ['chat', 'history', 'list', sessionId],
  queryFn: async ({ pageParam }): Promise<any> => {
    return (await getChatHistoryList({ ...pageParam })).data
  },
  getNextPageParam(lastPage: any[], allPages, lastPageParam): any {
    if (!lastPage?.length) return null
    return { ...lastPageParam, page: lastPageParam.page + 1 }
  },
  initialPageParam: { session_id: sessionId, page: 1, page_size: 20 }
})

// 获取保存聊天的记录
export const chatHistoryChatRecordQueryOption = (id, cb?) => infiniteQueryOptions({
  queryKey: ['chat', 'history', id],
  queryFn: async ({ pageParam }): Promise<any> => {
    const data = (await getChatHistoryById({ ...pageParam })).data?.chat_record || []
    data.length > 0 && cb && cb({ pageParam })

    return data
  },
  getNextPageParam(lastPage: any[], allPages, lastPageParam): any {
    if (!lastPage?.length) return null
    return {
      ...lastPageParam,
      page: lastPageParam.page + 1,
      last_time: lastPage[lastPage.length - 1]?.last_time,
      last_id: lastPage[lastPage.length - 1]?.id,

    }
  },
  initialPageParam: {
    page: 1,
    page_size: 20,
    id
  }
})

// 灵感回复
export const inspirationReplyQueryOption = (session_id, chat_id, scene_script_id?) => {
  return queryOptions({
    queryKey: ['inspirationReply', session_id, chat_id, scene_script_id],
    queryFn: () => getInspirationReply({ chat_id, session_id, scene_script_id })
  })
}

export const chatStyleQueryOption = (type) => {
  return infiniteQueryOptions({
    queryKey: ['chat', 'style', type],
    queryFn: async ({ pageParam }): Promise<any> => {
      return (await getChatStyleList({ ...pageParam })).data
    },
    getNextPageParam(lastPage: any[], allPages, lastPageParam): any {
      if (!lastPage?.length) return null
      return {
        ...lastPageParam,
        page: lastPageParam.page + 1,
      }
    },
    initialPageParam: {
      page: 1,
      page_size: 20,
      type
    }
  })
}

export const chatMemoryRolesQueryOption = (sessionId: string, chatId: string) => queryOptions({
  queryKey: ['chats', 'memory', 'roles', sessionId, chatId],
  queryFn: async (): Promise<ChatMemoryRolesData> => {
    const res = await getChatMemoryRoles({
      session_id: Number(sessionId),
      chat_id: chatId,
    })
    if (Number(res.status) !== 200) throw new Error(res.message || res.msg || '')
    return res.data
  },
})

export const chatMemoryDetailQueryOption = (roleId: number) => queryOptions({
  queryKey: ['chats', 'memory', 'detail', roleId],
  queryFn: async (): Promise<ChatMemoryAttribute[]> => {
    const res = await getChatMemoryDetail({ role_id: roleId })
    if (res.status !== 200) throw new Error(res.message || res.msg || '')
    return Array.isArray(res.data) ? res.data : []
  },
}) 